/**
 * ENGRAVED DESCRIPTIONS — an enlarged card is its resting card, scaled.
 *
 * Reported 2026-09-24 on an enemy board Ember Artisan in Firefox: its
 * description "morphs" while the hovered card grows, and "the text is supposed
 * to be engraved ... it's never ever supposed to move".
 *
 * The browser wraps a description at the size it lays it out at, and wrapping
 * is not scale-invariant: glyph advances land on device pixels, so a line that
 * just fills its box fits at one size and not at the next. Measured on "Shapes
 * fire as though it were clay, ..." at the owner's desktop scale (GNOME text
 * scaling 1.25: DPR 1.25, a 1536px page): "were" changed line at most steps of
 * every board grow, and the fully grown card wrapped differently from its
 * resting self at 14 of 35 page heights in Firefox, 23 in Chrome and 21 in
 * WebKit. The grow froze its lines, but only UNTIL it ended — letting go at
 * full size was the reflow the owner saw — and the other enlargements (hand
 * zoom, the crisp zoom's settle, the announce float, every popup) re-wrapped
 * as well.
 *
 * So an in-place enlargement ENGRAVES the resting lines as it starts and keeps
 * them until the card is back at rest, and a popup ADOPTS the lines and size
 * step of the card it enlarges. While engraved a description is frozen:
 * `Cards.fitCardDescription` leaves it alone, because its size and its lines
 * are the resting card's.
 *
 * An engraving is the SAME paragraph with its breaks forced: `white-space:
 * nowrap`, and each resting line an inline span ending in a `<br>`. It stays
 * one block with one inline layout, so the resting card paints exactly the
 * pixels of its natural paragraph — 0 differing in Chrome, Firefox and
 * WebKit at DPR 1, 1.25 and 2 — where one block per line (the July pin) was
 * off by a sub-pixel in WebKit, which snaps every block to device pixels. At
 * the enlarged size a line that fitted at rest can come out a hair wider than
 * its box (0.47px measured, Firefox at 2.86x); CSS then start-aligns that one
 * line, a shift of half that, where re-wrapping moved a whole word. The
 * whitespace between lines stays as text nodes (collapsed at a line's start),
 * so `textContent` is still the exact description; `data-orig` holds it too,
 * and releasing restores the original text node byte for byte. The class
 * name is historical: the pin used to hold only for the grow.
 *
 * THE TITLE IS ENGRAVED WITH IT. A title is fitted at its resting size, and
 * the same rounding re-fits it at any other: a single line that filled its
 * bar came out a hair wider and the bar (`nowrap`, `text-overflow: ellipsis`)
 * swapped its last letters for an ellipsis, and a two-line title could move a
 * word between its lines (filmed 2026-09-26: the carried copy of "Thunder
 * Caller" read "THUNDER CALL…" through its lift). An engraved title keeps its
 * size, clips instead of ellipsizing (a hair of overflow stays inside the
 * bar's side padding), and a two-line title keeps its lines the way a
 * description does.
 *
 * Contract: tests/unit/ui/cardDescriptionEngraving.test.js and
 * tests/e2e-playwright/card-desc-engraved.spec.js.
 */

let CardDescEngraving = {};

CardDescEngraving = (function () {
    const PIN_CLASS = 'card-desc-grow-pin';
    const TITLE_CLASS = 'card-name--engraved';
    const TITLE_PIN_CLASS = 'card-name-lines';
    // CSS white space. NOT `\s`: a no-break space (French "texte :") is ink
    // the browser never breaks at, and between two blocks it would render.
    const INK_RUN = /[^ \t\n\r\f]+/g;
    const ANGLE_IN_DEGREES = { deg: 1, rad: 180 / Math.PI, turn: 360, grad: 0.9 };
    /* The first fit a description has scheduled and not yet run. Engraving
       before it would freeze unfitted text, so an engraving runs it first. */
    const pendingFits = new WeakMap();

    function descOf(cardOrDesc) {
        if (!cardOrDesc || typeof cardOrDesc.querySelector !== 'function') return null;
        if (cardOrDesc.classList?.contains('card-desc')) return cardOrDesc;
        return cardOrDesc.querySelector('.card-desc');
    }

    function pinOf(descElement) {
        if (!descElement || typeof descElement.querySelector !== 'function') return null;
        return descElement.querySelector(`:scope > .${PIN_CLASS}`);
    }

    function isEngraved(cardOrDesc) {
        return Boolean(pinOf(descOf(cardOrDesc)));
    }

    // A card's own title (a direct child), or the title itself.
    function titleOf(cardOrTitle) {
        if (!cardOrTitle || typeof cardOrTitle.querySelector !== 'function') return null;
        if (cardOrTitle.classList?.contains('card-name')) return cardOrTitle;
        return cardOrTitle.querySelector(':scope > .card-name');
    }

    function isTitleEngraved(cardOrTitle) {
        return Boolean(titleOf(cardOrTitle)?.classList.contains(TITLE_CLASS));
    }

    function layoutText(layout) {
        let text = layout.prefix;
        layout.lines.forEach((line, index) => {
            if (index > 0) text += layout.separators[index - 1];
            text += line;
        });
        return text + layout.suffix;
    }

    /** The layout an engraving carries, read from its DOM (no layout needed). */
    function engravedLayout(descElement) {
        return layoutOfPin(pinOf(descElement));
    }

    function layoutOfPin(pin) {
        if (!pin) return null;
        const layout = { text: '', prefix: '', lines: [], separators: [], suffix: '' };
        let gap = '';
        for (const child of pin.childNodes) {
            if (child.nodeType === Node.ELEMENT_NODE) {
                if (layout.lines.length) layout.separators.push(gap);
                else layout.prefix = gap;
                gap = '';
                layout.lines.push(child.textContent);
            } else if (child.nodeType === Node.TEXT_NODE) {
                gap += child.data;
            }
        }
        if (!layout.lines.length) return null;
        layout.suffix = gap;
        layout.text = pin.dataset.orig != null ? pin.dataset.orig : layoutText(layout);
        return layout;
    }

    /* The on-screen box of a card is ROTATED in both hands' fans and leans
       under CardTilt, so "same line" cannot mean "same rect top": across a
       70px line a 6-degree fan tilts the tops by 7px, more than the gap
       between two lines of small text. Project onto the card's line-normal
       axis before grouping — every glyph box's centre is exactly its
       rotated centre, and translation is common to all of them. The enemy fan
       rotates with the individual `rotate`/`scale` properties, which the
       computed `transform` does not include, so those are read as well (CSS
       Transforms 2 order: translate, rotate, scale, transform). A 3D lean
       contributes its 2D part; a larger one fails the line-box cross-check and
       the description simply stays natural. */
    function linearTransformOf(style) {
        let m = [1, 0, 0, 1];
        const apply = (n) => {
            m = [
                m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
                m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
            ];
        };
        const rotate = String(style?.rotate || 'none').trim();
        if (rotate !== 'none') {
            const parts = rotate.split(/\s+/);
            const angle = /^(-?[\d.]+(?:e[-+]?\d+)?)(deg|rad|turn|grad)$/.exec(parts[parts.length - 1]);
            const axis = parts.slice(0, -1).join(' ');
            const aboutZ = axis === '' || axis === 'z' || axis === '0 0 1';
            if (angle && (aboutZ || axis === '0 0 -1')) {
                const degrees = Number(angle[1]) * ANGLE_IN_DEGREES[angle[2]] * (aboutZ ? 1 : -1);
                const radians = (degrees * Math.PI) / 180;
                apply([Math.cos(radians), Math.sin(radians), -Math.sin(radians), Math.cos(radians)]);
            }
        }
        const scale = String(style?.scale || 'none').trim();
        if (scale !== 'none') {
            const [sx, sy = sx] = scale.split(/\s+/).map(Number);
            if (Number.isFinite(sx) && Number.isFinite(sy)) apply([sx, 0, 0, sy]);
        }
        const transform = style?.transform;
        if (transform && transform !== 'none' && typeof DOMMatrix === 'function') {
            try {
                const t = new DOMMatrix(transform);
                apply([t.a, t.b, t.c, t.d]);
            } catch (_) { /* unparseable: treat as untransformed */ }
        }
        return m;
    }

    /**
     * The element's line axes on screen: `across` (down the lines) and
     * `along` (along a line), both in screen pixels, and `pitchScale`, the
     * screen pixels one CSS pixel of line pitch takes. A CSS `zoom` scales the
     * glyph boxes but not the computed `line-height`, and each element's
     * computed `zoom` is its own (measured in Chrome, Firefox and WebKit), so
     * every zoom up the chain multiplies in.
     */
    function localLineAxis(element) {
        let m = [1, 0, 0, 1];
        let zoom = 1;
        for (let node = element; node && node.nodeType === Node.ELEMENT_NODE; node = node.parentElement) {
            const style = getComputedStyle(node);
            const n = linearTransformOf(style);
            // An ancestor's transform applies after its descendant's.
            m = [
                n[0] * m[0] + n[2] * m[1], n[1] * m[0] + n[3] * m[1],
                n[0] * m[2] + n[2] * m[3], n[1] * m[2] + n[3] * m[3],
            ];
            const own = Number.parseFloat(style?.zoom);
            if (Number.isFinite(own) && own > 0) zoom *= own;
        }
        const [a, b, c, d] = m;
        const det = a * d - b * c;
        if (!Number.isFinite(det) || Math.abs(det) < 1e-6) {
            return { across: (x, y) => y, along: (x) => x, pitchScale: zoom };
        }
        // Keep distances in screen pixels, like the glyph-height tolerance.
        // An unnormalised inverse removed scale from the centres only: at
        // 3.8x it merged neighbouring lines, then pinned their combined text
        // onto a single overflowing line in the inspection popup.
        const length = Math.hypot(a, b);
        const normalLength = length * Math.sign(det);
        return {
            across: (x, y) => (a * y - b * x) / normalLength,
            along: (x, y) => (a * x + b * y) / length,
            pitchScale: (zoom * Math.abs(det)) / length,
        };
    }

    function centreOn(project, rect) {
        const x = (Number(rect.left) || 0) + (Number(rect.width) || 0) / 2;
        const y = (Number(rect.top) || 0) + (Number(rect.height) || 0) / 2;
        return project(x, y);
    }

    /**
     * Where the browser broke the description as it lies now, as exact slices
     * of its text. Null when it cannot be read reliably (not laid out, not one
     * text node, a line that runs back on itself, or glyph boxes that disagree
     * with the engine's line boxes); the caller then leaves the natural
     * paragraph in place.
     */
    function measure(descElement) {
        const node = descElement?.firstChild;
        if (!node || node.nodeType !== Node.TEXT_NODE || node.nextSibling) return null;
        const text = node.data;
        if (!text.trim() || typeof document.createRange !== 'function') return null;
        const range = document.createRange();
        if (typeof range.getBoundingClientRect !== 'function'
            || typeof range.getClientRects !== 'function') return null;
        const axis = localLineAxis(descElement);
        const isHigh = (index) => {
            const code = text.charCodeAt(index);
            return code >= 0xd800 && code <= 0xdbff && index + 1 < text.length;
        };
        const isLow = (index) => {
            const code = text.charCodeAt(index);
            return code >= 0xdc00 && code <= 0xdfff;
        };
        const nextIndex = (index) => index + (isHigh(index) ? 2 : 1);
        // One code point's glyph box: its centre down the lines and along them.
        const glyphAt = (index) => {
            range.setStart(node, index);
            range.setEnd(node, nextIndex(index));
            const r = range.getBoundingClientRect();
            if (!r || !(r.height > 0)) return null;
            return { y: centreOn(axis.across, r), x: centreOn(axis.along, r), height: r.height };
        };
        const runs = [];
        let match;
        INK_RUN.lastIndex = 0;
        while ((match = INK_RUN.exec(text))) runs.push([match.index, match.index + match[0].length]);
        if (!runs.length) return null;
        const first = glyphAt(runs[0][0]);
        if (!first) return null;
        /* HALF THE LINE PITCH: lines sit exactly one pitch apart, so a glyph
           within half of it is on this line, whatever its own box — a
           fallback glyph (an emoji) moves its box far less. Half a GLYPH box
           was the ruler, and Spectral's box (1.52em) is taller than its pitch
           (1.2em): at a phone hand's 2.7px text the engine rounds it to whole
           pixels, 4px against a 3.23px pitch, which left about a pixel between
           the next line and this one. On an iPhone that pixel went, and an
           enlarged Thorn Snare showed its first two resting lines pinned as
           one: "Thorny vines whip out and sn" over "mid-step." (owner, 28 Oct
           2026: "card description text getting cropped out on mobile").
           A line-height not given in pixels keeps the half glyph box. */
        const pitch = Number.parseFloat(getComputedStyle(descElement).lineHeight) * axis.pitchScale;
        const tolerance = Number.isFinite(pitch) && pitch > 0 ? pitch / 2 : first.height / 2;
        const starts = [runs[0][0]];
        const ends = [];
        let lineY = first.y;
        // Where this line's ink has reached along it. Text runs one way along
        // a line, so a run that starts back behind it is on the next line: a
        // reading that keeps it on this one has read two lines as one.
        let lineX = first.x;
        let inkEnd = runs[0][0];
        for (const [runStart, runEnd] of runs) {
            let cursor = runStart;
            for (;;) {
                const head = glyphAt(cursor);
                if (!head) return null;
                if (head.y > lineY + tolerance) {
                    ends.push(inkEnd);
                    starts.push(cursor);
                    lineY = head.y;
                } else if (head.x < lineX - tolerance) {
                    return null;
                }
                lineX = head.x;
                let tailIndex = runEnd - 1;
                if (isLow(tailIndex) && tailIndex > cursor) tailIndex -= 1;
                const tail = glyphAt(tailIndex);
                if (!tail) return null;
                if (tail.y <= lineY + tolerance) {
                    lineX = tail.x;
                    inkEnd = runEnd;
                    break;
                }
                // The run itself breaks (text written without spaces): find
                // the first code point on the next line.
                let low = cursor;
                let high = tailIndex;
                while (nextIndex(low) < high) {
                    let mid = Math.floor((low + high) / 2);
                    if (isLow(mid)) mid -= 1;
                    if (mid <= low) break;
                    const probe = glyphAt(mid);
                    if (!probe) return null;
                    if (probe.y <= lineY + tolerance) low = mid;
                    else high = mid;
                }
                inkEnd = nextIndex(low);
                cursor = inkEnd;
            }
        }
        ends.push(inkEnd);

        // Cross-check against the engine's own line boxes.
        range.selectNodeContents(descElement);
        const lineBoxes = [];
        for (const r of range.getClientRects()) {
            if (!(r.width > 0) || !(r.height > 0)) continue;
            const y = centreOn(axis.across, r);
            if (!lineBoxes.some((seen) => Math.abs(seen - y) <= tolerance)) lineBoxes.push(y);
        }
        if (lineBoxes.length !== starts.length) return null;

        const layout = { text, prefix: text.slice(0, starts[0]), lines: [], separators: [], suffix: '' };
        starts.forEach((start, index) => {
            if (index > 0) layout.separators.push(text.slice(ends[index - 1], start));
            layout.lines.push(text.slice(start, ends[index]));
        });
        layout.suffix = text.slice(ends[ends.length - 1]);
        return layoutText(layout) === text ? layout : null;
    }

    /**
     * Freeze a card's description on the lines it shows now. Call it while the
     * card is at REST, as an enlargement starts; an engraved description keeps
     * its engraving (the resting lines win over anything later).
     * @param {HTMLElement} cardElement the card (or its `.card-desc`)
     * @param {{ layout?: object }} [options] lines to engrave instead of
     *   measuring; ignored unless they spell the description exactly
     * @returns {boolean} whether the description is engraved afterwards
     */
    function engrave(cardElement, options = {}) {
        // A card's title is engraved with it; `options` describe the description.
        engraveTitle(cardElement);
        return engraveDescription(cardElement, options);
    }

    // An engraving runs the first fit its element has scheduled: freezing
    // before it would freeze unfitted text.
    function runPendingFit(element) {
        const firstFit = pendingFits.get(element);
        if (!firstFit) return;
        pendingFits.delete(element);
        firstFit();
    }

    /* The same text with its breaks forced: one block, `nowrap`, each line an
       inline span ending in a `<br>`. `font: inherit` on every element: a
       universal font rule must never restyle the spans away from the face. */
    function pinnedLines(className, text, layout) {
        const pin = document.createElement('span');
        pin.className = className;
        pin.style.cssText = 'display:block;white-space:nowrap;font:inherit;';
        pin.dataset.orig = text;
        if (layout.prefix) pin.appendChild(document.createTextNode(layout.prefix));
        layout.lines.forEach((line, index) => {
            const gap = index > 0 ? layout.separators[index - 1] : '';
            if (gap) pin.appendChild(document.createTextNode(gap));
            const lineElement = document.createElement('span');
            lineElement.style.cssText = 'font:inherit;';
            lineElement.textContent = line;
            if (index < layout.lines.length - 1) lineElement.appendChild(document.createElement('br'));
            pin.appendChild(lineElement);
        });
        if (layout.suffix) pin.appendChild(document.createTextNode(layout.suffix));
        return pin;
    }

    function engraveDescription(cardElement, { layout: given = null } = {}) {
        const descElement = descOf(cardElement);
        if (!descElement) return false;
        if (pinOf(descElement)) return true;
        const text = descElement.textContent;
        if (!text || !text.trim()) return false;
        let layout = given && layoutText(given) === text ? given : null;
        if (!layout) {
            runPendingFit(descElement);
            layout = measure(descElement);
        }
        if (!layout) return false;
        descElement.textContent = '';
        descElement.appendChild(pinnedLines(PIN_CLASS, text, layout));
        return true;
    }

    /**
     * Freeze a card's title as it rests now: its size stays (it is written in
     * card-height units), it clips rather than ellipsizes, and a two-line title
     * keeps its lines. Call it with the card at REST, as an enlargement starts.
     * @param {HTMLElement} cardOrTitle the card (or its `.card-name`)
     * @param {{ layout?: object }} [options] lines to engrave instead of measuring
     * @returns {boolean} whether the title is engraved afterwards
     */
    function engraveTitle(cardOrTitle, { layout: given = null } = {}) {
        const name = titleOf(cardOrTitle);
        if (!name) return false;
        if (name.classList.contains(TITLE_CLASS)) return true;
        const text = name.textContent;
        if (!text || !text.trim()) return false;
        runPendingFit(name);
        if (name.classList.contains('card-name--wrap')) {
            const layout = given && layoutText(given) === text ? given : measure(name);
            // An unreadable layout keeps the natural title (it still clips).
            if (layout && layout.lines.length > 1) {
                name.textContent = '';
                name.appendChild(pinnedLines(TITLE_PIN_CLASS, text, layout));
            }
        }
        name.classList.add(TITLE_CLASS);
        return true;
    }

    function releaseTitle(cardOrTitle) {
        const name = titleOf(cardOrTitle);
        if (!name || !name.classList.contains(TITLE_CLASS)) return false;
        const pin = name.querySelector(`:scope > .${TITLE_PIN_CLASS}`);
        if (pin) name.textContent = pin.dataset.orig != null ? pin.dataset.orig : pin.textContent;
        name.classList.remove(TITLE_CLASS);
        return true;
    }

    /** What a copy needs to show this card's title exactly as it rests. */
    function titleStateOf(cardOrTitle) {
        const name = titleOf(cardOrTitle);
        if (!name) return null;
        const pin = name.querySelector(`:scope > .${TITLE_PIN_CLASS}`);
        const wrapped = name.classList.contains('card-name--wrap');
        return {
            text: pin?.dataset.orig ?? name.textContent,
            size: name.style.fontSize,
            wrapped,
            layout: wrapped ? (layoutOfPin(pin) || measure(name)) : null,
        };
    }

    function adoptTitle(targetCard, state) {
        const name = titleOf(targetCard);
        if (!name || !state) return false;
        const text = name.querySelector(`:scope > .${TITLE_PIN_CLASS}`)?.dataset.orig ?? name.textContent;
        if (state.text !== text) return false;
        releaseTitle(name);
        // The copy's own first fit would size it for itself; it is replaced.
        pendingFits.delete(name);
        if (/var\(--card-height\)/.test(state.size || '')) name.style.fontSize = state.size;
        name.classList.toggle('card-name--wrap', Boolean(state.wrapped));
        return engraveTitle(name, { layout: state.layout });
    }

    /** Back to the natural text — only once the card is at rest again. */
    function release(cardElement) {
        releaseTitle(cardElement);
        const descElement = descOf(cardElement);
        const pin = pinOf(descElement);
        if (!pin) return false;
        descElement.textContent = pin.dataset.orig != null ? pin.dataset.orig : pin.textContent;
        return true;
    }

    /** The resting layout a copy of this card should adopt, or null. */
    function layoutOf(cardElement) {
        const descElement = descOf(cardElement);
        if (!descElement) return null;
        return engravedLayout(descElement) || measure(descElement);
    }

    /**
     * Give an enlarged COPY of a card (a popup, a re-rendered hand card) the
     * source card's description: the same size step in card units and the same
     * lines, engraved — and its title, the same way. Nothing happens to either
     * unless both say the same text.
     * @param {HTMLElement} targetCard the copy
     * @param {HTMLElement|{layout: object, fontSize?: string, title?: object}} source
     *   the card it enlarges, or a layout read from it earlier (`layoutOf`)
     *   plus its `.card-desc` inline font size and its `titleStateOf`
     * @returns {boolean} whether the copy now carries the source's engraving
     */
    function adopt(targetCard, source) {
        // The title comes over too: its size, its lines, engraved.
        if (source instanceof Element) {
            const sourceTitle = titleOf(source);
            if (sourceTitle && sourceTitle !== titleOf(targetCard)) adoptTitle(targetCard, titleStateOf(sourceTitle));
        } else if (source?.title) {
            adoptTitle(targetCard, source.title);
        }
        const target = descOf(targetCard);
        if (!target || !source) return false;
        let layout = null;
        let fontSize = '';
        if (source instanceof Element) {
            const sourceDesc = descOf(source);
            if (!sourceDesc || sourceDesc === target) return false;
            layout = layoutOf(sourceDesc);
            fontSize = sourceDesc.style.fontSize;
        } else {
            layout = source.layout || null;
            fontSize = source.fontSize || '';
        }
        if (!layout) return false;
        const targetText = pinOf(target)?.dataset.orig ?? target.textContent;
        if (layout.text !== targetText) return false;
        release(target);
        // The copy's own first fit would size it for itself; it is replaced.
        pendingFits.delete(target);
        if (/var\(--card-unit\)/.test(fontSize)) target.style.fontSize = fontSize;
        return engrave(target, { layout });
    }

    /** Cards registers a description's or a title's first scheduled fit here. */
    function setPendingFit(element, run) {
        if (element && typeof run === 'function') pendingFits.set(element, run);
    }

    /** Forget a scheduled first pass (only `run`'s, when one is named). */
    function clearPendingFit(element, run = null) {
        if (!element) return;
        if (run && pendingFits.get(element) !== run) return;
        pendingFits.delete(element);
    }

    return {
        PIN_CLASS,
        TITLE_CLASS,
        isEngraved,
        isTitleEngraved,
        engrave,
        release,
        adopt,
        layoutOf,
        titleStateOf,
        measure,
        setPendingFit,
        clearPendingFit,
    };
})();

export { CardDescEngraving };
